/**
 * ProductCalibrationsTab - Manage calibrations linked to a product
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/userPermissions';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import productsService, {
  ProductCalibrationLink,
  CalibrationListItem,
} from '../services/products';
import calibrationsService from '../services/calibrations';
import { apiErrorText } from '../utils/apiError';
import { rValue } from '../utils/regressionConversion';

const fmtRange = (min?: number | null, max?: number | null, unit?: string | null): string =>
  min == null || max == null ? '-' : `${Math.round(min)} to ${Math.round(max)}${unit ? ` ${unit}` : ''}`;

// Product2Calibration.Status codes (legacy workflow)
const LINK_STATUS_LABELS: Record<string, string> = {
  '1': 'In Progress',
  '2': 'Finished',
};

interface ProductCalibrationsTabProps {
  productId: string;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  /** Embedded on the mix page: open a calibration in place instead of navigating. */
  onSelect?: (calibrationId: string) => void;
  /** Embedded: start a new calibration in place. */
  onNew?: () => void;
  /** The calibration open in the workspace (row highlighted). */
  selectedId?: string | null;
  /** Bump to reload the links (the workspace changed something). */
  refreshKey?: number;
  /** Tell the host after links load or change. */
  onLinksChanged?: (links: ProductCalibrationLink[]) => void;
  /** Mix design page: one-line active curve, the 3 most relevant rows, "Show N more". */
  compact?: boolean;
}

const COMPACT_ROWS = 3;

/** Active first, then finished, then the most test results. */
const relevantFirst = (links: ProductCalibrationLink[]): ProductCalibrationLink[] =>
  [...links].sort((a, b) =>
    Number(!!b.is_active) - Number(!!a.is_active)
    || Number(b.status === '2') - Number(a.status === '2')
    || (b.summary?.sample_count ?? 0) - (a.summary?.sample_count ?? 0));

const ProductCalibrationsTab: React.FC<ProductCalibrationsTabProps> = ({
  productId,
  onError,
  onSuccess,
  onSelect,
  onNew,
  selectedId,
  refreshKey,
  onLinksChanged,
  compact = false,
}) => {
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  // Mirrors the backend gates (utils/rbac.py): the server refuses these actions
  // for other roles, so they are not offered either.
  const canCreate = hasPermission(user, 'mix_designs.create_maturity_curve');
  const canLink = hasPermission(user, 'mix_designs.link_activate');
  const [links, setLinks] = useState<ProductCalibrationLink[]>([]);
  const [calibrations, setCalibrations] = useState<CalibrationListItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Link dialog
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedCalibrationId, setSelectedCalibrationId] = useState('');
  const [linkIsActive, setLinkIsActive] = useState(false);

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<ProductCalibrationLink | null>(null);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productsService.listProductCalibrations(productId);
      setLinks(data);
      onLinksChanged?.(data);
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to load calibration links');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, onError, refreshKey]);

  const loadCalibrations = useCallback(async () => {
    try {
      const data = await productsService.listCalibrations();
      setCalibrations(data);
    } catch (err: any) {
      console.error('Failed to load calibrations:', err);
    }
  }, []);

  useEffect(() => {
    loadLinks();
    loadCalibrations();
  }, [loadLinks, loadCalibrations]);

  const handleLinkCalibration = async () => {
    if (!selectedCalibrationId) {
      onError('Please select a calibration');
      return;
    }

    setLoading(true);
    try {
      await productsService.linkCalibration(productId, {
        calibration_id: selectedCalibrationId,
        is_active: linkIsActive,
      });
      onSuccess('Calibration linked successfully');
      setLinkDialogOpen(false);
      setSelectedCalibrationId('');
      setLinkIsActive(false);
      loadLinks();
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to link calibration');
    } finally {
      setLoading(false);
    }
  };

  // Activation changes the strength every new pour of this mix shows, so it is confirmed.
  const [activateLink, setActivateLink] = useState<ProductCalibrationLink | null>(null);

  const handleToggleActive = async (link: ProductCalibrationLink) => {
    if (!link.is_active && activateLink?.id !== link.id) {
      setActivateLink(link);
      return;
    }
    setActivateLink(null);
    setLoading(true);
    try {
      if (!link.is_active) {
        // Activating runs the FULL workflow (legacy ActiveCalibration):
        // recalculates the regression and rewrites the product's default
        // targets with the chosen curve — not just the link flag.
        const result = await calibrationsService.activate(link.calibration_id, productId);
        onSuccess(result.message || 'Calibration activated');
      } else {
        await productsService.updateCalibrationLink(productId, link.id, {
          is_active: false,
        });
        onSuccess('Calibration deactivated. Targets keep their last calculated values.');
      }
      loadLinks();
    } catch (err: any) {
      onError(apiErrorText(err, 'Failed to update calibration link'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (link: ProductCalibrationLink) => {
    setLinkToDelete(link);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!linkToDelete) return;

    setLoading(true);
    try {
      await productsService.deleteCalibrationLink(productId, linkToDelete.id);
      onSuccess('Calibration unlinked');
      setDeleteConfirmOpen(false);
      setLinkToDelete(null);
      loadLinks();
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to unlink calibration');
    } finally {
      setLoading(false);
    }
  };

  // Filter out already-linked calibrations
  const linkedCalIds = new Set(links.map(l => l.calibration_id));
  const availableCalibrations = calibrations.filter(c => !linkedCalIds.has(c.id));

  return (
    <Box>
      {/* Section title comes from the host page (ProductDetail card) */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: compact ? 1 : 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canCreate && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => (onNew ? onNew() : navigate(`/calibrations/new?productId=${productId}`))}
              sx={{ textTransform: 'none' }}
            >
              {compact ? 'New' : 'New Calibration'}
            </Button>
          )}
          {canLink && (
            <Button
              variant={compact ? 'outlined' : 'contained'}
              size="small"
              startIcon={<LinkIcon />}
              onClick={() => setLinkDialogOpen(true)}
              disabled={loading}
              sx={{ textTransform: 'none' }}
            >
              {compact ? 'Link existing' : 'Link Calibration'}
            </Button>
          )}
        </Box>
      </Box>

      {compact && links.length > 0 && (() => {
        const active = links.find((l) => l.is_active);
        const sm = active?.summary;
        const r = rValue(sm?.r2);
        return (
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 1, mb: 1.5 }}>
            {active ? (
              <Typography variant="body2">
                <strong>Active:</strong> {active.calibration_name || active.calibration_code}
                <Typography component="span" variant="body2" color="text.secondary">
                  {sm ? ` · ${sm.curve_type === 'linear' ? 'linear' : 'logarithmic'}${r != null ? `, R ${r.toFixed(2)}` : ''}` : ''}
                  {sm && sm.maturity_min != null ? ` · tested ${fmtRange(sm.maturity_min, sm.maturity_max, sm.unit)}` : ''}
                </Typography>
              </Typography>
            ) : (
              <Typography variant="body2" color="warning.main">
                None active: pours of this mix show maturity but no strength.
              </Typography>
            )}
          </Box>
        );
      })()}

      {!compact && (() => {
        const active = links.find((l) => l.is_active);
        const sm = active?.summary;
        if (!active) {
          return links.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No calibration is active, so pours of this mix show maturity but no estimated strength.
              Finish a calibration and make it active.
            </Alert>
          ) : null;
        }
        const r = rValue(sm?.r2);
        if (compact) {
          return null;
        }
        return (
          <Alert severity="success" icon={false} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Active curve:</strong> {active.calibration_name || active.calibration_code}
              {sm ? `, ${sm.curve_type === 'linear' ? 'linear' : 'logarithmic'}${r != null ? ` (R = ${r.toFixed(2)})` : ''}` : ''}
            </Typography>
            {sm && (
              <Typography variant="caption" display="block">
                Tested range {fmtRange(sm.maturity_min, sm.maturity_max, sm.unit)} from {sm.ages_tested} test age
                {sm.ages_tested === 1 ? '' : 's'}. {sm.basis_text ? `Maturity equation: ${sm.basis_text}.` : ''}
                {' '}Strengths outside the tested range are shown as below or above it.
              </Typography>
            )}
          </Alert>
        );
      })()}

      {links.length === 0 ? (
        <Alert severity="info">No calibrations linked to this product.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size={compact ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Test ages</TableCell>
                {!compact && <TableCell>Tested maturity range</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(compact && !showAll ? relevantFirst(links).slice(0, COMPACT_ROWS) : compact ? relevantFirst(links) : links).map((link) => (
                <TableRow
                  key={link.id}
                  selected={!!selectedId && link.calibration_id === selectedId}
                  hover={!!onSelect}
                  sx={onSelect ? { cursor: 'pointer' } : undefined}
                  onClick={onSelect ? () => onSelect(link.calibration_id) : undefined}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        {link.calibration_name || link.calibration_code || link.calibration_id}
                      </Typography>
                      {(link.version_no ?? 1) > 1 && (
                        <Chip label={`v${link.version_no}`} size="small" variant="outlined" />
                      )}
                    </Box>
                    {link.calibration_name && (
                      <Typography variant="caption" color="text.secondary">{link.calibration_code}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {link.summary ? (link.summary.calibration_type === 's' ? 'System' : 'Manual') : '-'}
                  </TableCell>
                  <TableCell>
                    {link.summary ? (
                      <Tooltip title="ASTM C1074 recommends 5 test ages (1, 3, 7, 14 and 28 days)">
                        <Chip
                          size="small"
                          variant="outlined"
                          color={link.summary.ages_tested >= link.summary.ages_recommended ? 'success' : 'default'}
                          label={`${link.summary.ages_tested} of ${link.summary.ages_recommended}`}
                        />
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  {!compact && (
                    <TableCell>
                      {link.summary ? fmtRange(link.summary.maturity_min, link.summary.maturity_max, link.summary.unit) : '-'}
                    </TableCell>
                  )}
                  <TableCell>
                    {link.status && (
                      <Chip
                        label={LINK_STATUS_LABELS[link.status] || link.status}
                        size="small"
                        color={link.status === '2' ? 'success' : 'default'}
                      />
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {/* Activation requires a finished calibration (server 409s otherwise) */}
                    <Tooltip
                      title={!link.is_active && link.status !== '2'
                        ? 'Finish this calibration before activating'
                        : ''}
                    >
                      <span>
                        <Switch
                          checked={!!link.is_active}
                          onChange={() => handleToggleActive(link)}
                          disabled={loading || !canLink || (!link.is_active && link.status !== '2')}
                          size="small"
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onSelect) onSelect(link.calibration_id);
                        else navigate(`/calibrations/${link.calibration_id}?productId=${productId}`);
                      }}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                    {canLink && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(link)}
                        disabled={loading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {compact && links.length > COMPACT_ROWS && (
        <Button size="small" sx={{ mt: 0.5, textTransform: 'none' }} onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show fewer' : `Show ${links.length - COMPACT_ROWS} more`}
        </Button>
      )}

      {/* Link Calibration Dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Calibration to Product</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Calibration</InputLabel>
              <Select
                value={selectedCalibrationId}
                onChange={(e) => setSelectedCalibrationId(e.target.value)}
                label="Calibration"
              >
                {availableCalibrations.length === 0 ? (
                  <MenuItem disabled>No available calibrations</MenuItem>
                ) : (
                  availableCalibrations.map((cal) => (
                    <MenuItem key={cal.id} value={cal.id}>
                      {cal.code} {cal.name ? `- ${cal.name}` : ''}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={linkIsActive}
                  onChange={(e) => setLinkIsActive(e.target.checked)}
                />
              }
              label="Set as active calibration"
            />
            <Typography variant="caption" color="text.secondary">
              Only a finished calibration can be made active. Link a calibration that is in progress first, then finish it and activate it.
            </Typography>
            {linkIsActive && (
              <Alert severity="warning" variant="outlined">
                Setting this as active will deactivate any currently active calibration for this product.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleLinkCalibration} variant="contained" disabled={loading || !selectedCalibrationId}>
            Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate confirmation */}
      <Dialog open={!!activateLink} onClose={() => setActivateLink(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Make this calibration active?</DialogTitle>
        <DialogContent>
          <Typography>
            New pours of this mix design will estimate strength from{' '}
            <strong>{activateLink?.calibration_name || activateLink?.calibration_code}</strong>, and the strength
            targets of the mix are recalculated from its curve. The calibration that is active now becomes inactive.
            Running pours keep the calibration they started with.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateLink(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={loading}
            onClick={() => activateLink && handleToggleActive(activateLink)}
          >
            Make active
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Unlink</DialogTitle>
        <DialogContent>
          {linkToDelete?.is_active ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              Cannot unlink an active calibration. Deactivate it first.
            </Alert>
          ) : (
            <Typography>
              Are you sure you want to unlink calibration <strong>{linkToDelete?.calibration_code}</strong> from this product?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading || !!linkToDelete?.is_active}
          >
            Unlink
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductCalibrationsTab;
